import { useCallback, useEffect, useState } from "react";
import { isAxiosError } from "axios";
import { getPublicWebsiteAsync } from "../api/OrganizationApi";
import { PublicWebsiteResponse } from "../models/WebsiteModels";

interface UsePublicSiteReturn {
  website: PublicWebsiteResponse | null;
  loading: boolean;
  error: string | null;
  /** True when the backend says the slug does not exist (HTTP/envelope 404). */
  notFound: boolean;
  reload: () => Promise<void>;
}

export const usePublicSite = (
  slug: string | undefined,
): UsePublicSiteReturn => {
  const [website, setWebsite] = useState<PublicWebsiteResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!slug) {
      setWebsite(null);
      setLoading(false);
      setError("Website not found");
      return;
    }

    setLoading(true);
    setNotFound(false);
    try {
      const res = await getPublicWebsiteAsync(slug);
      if (res.success && res.result) {
        setWebsite(res.result);
        setError(null);
      } else if (res.responseCode === 404) {
        // Backend quirk: "not found" arrives as HTTP 200 with a
        // { success: false, responseCode: 404 } envelope.
        setNotFound(true);
      } else {
        setError(res.message || "Failed to load website");
      }
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        setNotFound(true);
      } else {
        console.error(err);
        setError("Failed to load website");
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  return { website, loading, error, notFound, reload };
};
